import {
  useFormContext,
  type FieldPathByValue,
  type FieldValues,
  type PathValue,
} from 'react-hook-form';

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { instantFor, marketInputInstant, type Occurrence } from '@/shared/lib/datetime-local';
import { zoneLabelOn } from '@/shared/lib/time-zone';

/**
 * A date-and-time field entered in a market's zone (the record's market), for forms with a validity
 * window. It names the zone with the offset on the entered date, and when the entered time happens
 * twice (the clocks go back) it asks which one is meant. A time that does not exist is reported by
 * the form's schema through `refineMarketInput`.
 */
export function MarketDateTimeField<TValues extends FieldValues>({
  name,
  occurrenceName,
  label,
  zone,
  original,
}: {
  name: FieldPathByValue<TValues, string>;
  occurrenceName: FieldPathByValue<TValues, Occurrence | null>;
  label: string;
  zone: string;
  /** The saved instant, which an untouched field sends back unchanged. */
  original?: string | null;
}) {
  const form = useFormContext<TValues>();

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => {
        const input = instantFor(original, field.value as string, zone);
        return (
          <FormItem>
            <FormLabel>{label}</FormLabel>
            <FormControl>
              <Input
                type="datetime-local"
                {...field}
                onChange={(event) => {
                  field.onChange(event);
                  // A choice made for another time would silently apply to this one.
                  form.setValue(occurrenceName, null as PathValue<TValues, typeof occurrenceName>);
                }}
              />
            </FormControl>
            <FormDescription>{zoneLabelOn(zone, marketInputInstant(input))}</FormDescription>
            <FormMessage />

            {input.kind === 'ambiguous' && (
              <FormField
                control={form.control}
                name={occurrenceName}
                render={({ field: choice }) => (
                  <FormItem className="space-y-1.5">
                    <FormLabel>{input.time} occurs twice:</FormLabel>
                    <FormControl>
                      <RadioGroup
                        aria-label={`${input.time} occurs twice`}
                        className="flex flex-wrap gap-x-4 gap-y-1.5"
                        value={(choice.value as Occurrence | null) ?? ''}
                        onValueChange={(value) => choice.onChange(value as Occurrence)}
                      >
                        <OccurrenceOption
                          id={`${name}-earlier`}
                          value="earlier"
                          label={`First (${input.earlierOffset})`}
                        />
                        <OccurrenceOption
                          id={`${name}-later`}
                          value="later"
                          label={`Second (${input.laterOffset})`}
                        />
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </FormItem>
        );
      }}
    />
  );
}

function OccurrenceOption({ id, value, label }: { id: string; value: Occurrence; label: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <RadioGroupItem id={id} value={value} />
      <Label htmlFor={id} className="text-xs font-normal">
        {label}
      </Label>
    </div>
  );
}
